// Wrapper sobre Umami (cloud.umami.is). O script eh carregado em
// index.html como <script defer>; quando ele termina de baixar, expoe
// `window.umami` com `.track(name, props)`. Antes disso, ou se o
// usuario bloqueou via adblocker, `window.umami` nao existe.
//
// Trata os dois casos sem quebrar a app:
//   - umami nao carregou (race com primeira chamada ou bloqueado): vira no-op
//   - umami carregou parcialmente / .track ausente: vira no-op
//
// Tambem nunca propaga excecao do tracking pro caller: se Umami der
// throw por qualquer motivo, a gente loga em dev e segue.

type EventProps = Record<string, string | number | boolean | null | undefined>;

interface UmamiAPI {
  track?: (name: string, props?: EventProps) => void;
}

declare global {
  interface Window {
    umami?: UmamiAPI;
  }
}

const isDev = import.meta.env.DEV;

/**
 * Eventos do funil. Lista canonica — usar tipo aqui evita typo em string
 * solta que silenciosamente nao reporta nada.
 */
export type FunnelEvent =
  | 'experience_card_view'
  | 'experience_card_click'
  | 'experience_detail_view'
  | 'booking_started'
  | 'booking_date_selected'
  | 'booking_slot_selected'
  | 'booking_customer_step_view'
  | 'phone_verification_started'
  | 'phone_verification_completed'
  | 'booking_review_view'
  | 'booking_submit_clicked'
  | 'instant_payment_started'
  | 'onrequest_submitted'
  | 'whatsapp_clicked'
  | 'booking_confirmation_view';

/**
 * Dispara evento. Props sao opcionais; valores undefined sao filtrados pra
 * nao poluir o payload (Umami trata undefined como string "undefined").
 */
export function trackEvent(name: FunnelEvent, props?: EventProps): void {
  try {
    const umami = window.umami;
    if (!umami || typeof umami.track !== 'function') {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.debug('[track] umami nao carregado, no-op:', name, props);
      }
      return;
    }

    const cleaned = props ? cleanProps(props) : undefined;
    umami.track(name, cleaned);

    if (isDev) {
      // eslint-disable-next-line no-console
      console.debug('[track]', name, cleaned ?? '');
    }
  } catch (err) {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.warn('[track] falha:', err);
    }
  }
}

function cleanProps(props: EventProps): EventProps {
  const out: EventProps = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined || v === null) continue;
    out[k] = v;
  }
  return out;
}
